import { useEffect, useState } from "react";
import type React from "react";
import type { VideoEvent } from "../../../../utilities/useTimestamps";
import styles from "./VideoControls.module.css";
import { addFullScreenClass } from "./videoControlHelper";
import i18n from "../../../../../i18n";
import { getDurationAriaLabelFromMs } from "../../../../../helpers/time";

interface QuestionSegment {
	questionNo: number | null; // null = pre/gap/post segment
	startMs: number;
	endMs: number;
}

function buildSegments(events: VideoEvent[], durationMs: number, timeOffsetMs: number): QuestionSegment[] {
	const sorted = events
		.filter(
			(e) =>
				e.type === "assessment" && e.name === "attempt question" && typeof e.details?.metadata?.duration === "number",
		)
		.sort((a, b) => a.time - b.time);

	if (sorted.length === 0) return [];

	const out: QuestionSegment[] = [];
	let cur = timeOffsetMs;

	for (const ev of sorted) {
		const start = ev.time * 1000;
		if (start >= durationMs) {
			break;
		}
		const end = Math.min(start + (ev.details.metadata.duration as number), durationMs);

		// gap / pre-question segment
		if (start > cur + 100) {
			out.push({ questionNo: null, startMs: cur, endMs: Math.min(start, durationMs) });
		}

		if (end > start) {
			out.push({ questionNo: ev.details.questionNo as number, startMs: start, endMs: end });
		}
		cur = end;
		if (cur >= durationMs) {
			break;
		}
	}

	// post-question remainder
	if (cur < durationMs - 100) {
		out.push({ questionNo: null, startMs: cur, endMs: durationMs });
	}

	return out;
}

// ── Gap-aware pixel utilities ─────────────────────────────────────────────────

const GAP_PX = 2; // must match CSS gap on .track

/** Returns the [startPx, endPx) range for each segment given the track's actual pixel width. */
function segmentPixelRanges(segs: QuestionSegment[], totalMs: number, W: number): { startPx: number; endPx: number }[] {
	const usable = Math.max(0, W - (segs.length - 1) * GAP_PX);
	const ranges: { startPx: number; endPx: number }[] = [];
	let cur = 0;
	for (const seg of segs) {
		const px = ((seg.endMs - seg.startMs) / totalMs) * usable;
		ranges.push({ startPx: cur, endPx: cur + px });
		cur += px + GAP_PX;
	}
	return ranges;
}

/** Maps a time value to a left-percentage within the track (gap-aware). */
function msToTrackPercent(
	ms: number,
	segs: QuestionSegment[],
	totalMs: number,
	W: number,
	timeOffsetMs: number,
): number {
	if (W === 0) return 0;
	if (segs.length === 0) return ((ms - timeOffsetMs) / totalMs) * 100;
	const ranges = segmentPixelRanges(segs, totalMs, W);
	for (let i = 0; i < segs.length; i++) {
		const { startMs, endMs } = segs[i];
		if (ms >= startMs && ms <= endMs) {
			const { startPx, endPx } = ranges[i];
			const t = endMs > startMs ? (ms - startMs) / (endMs - startMs) : 0;
			return ((startPx + t * (endPx - startPx)) / W) * 100;
		}
	}
	if (ms < segs[0].startMs) return (ranges[0].startPx / W) * 100;
	return (ranges[ranges.length - 1].endPx / W) * 100;
}

/** Maps a cursor pixel x back to a time in ms (gap-aware). Falls back to linear if in a gap. */
function pixelToMs(
	x: number,
	ranges: { startPx: number; endPx: number }[],
	segs: QuestionSegment[],
	timeOffsetMs: number,
	totalMs: number,
	W: number,
): number {
	if (segs.length === 0) return timeOffsetMs + (x / W) * totalMs;

	for (let i = 0; i < ranges.length; i++) {
		if (x >= ranges[i].startPx && x < ranges[i].endPx) {
			const t =
				ranges[i].endPx > ranges[i].startPx ? (x - ranges[i].startPx) / (ranges[i].endPx - ranges[i].startPx) : 0;
			return segs[i].startMs + t * (segs[i].endMs - segs[i].startMs);
		}
	}
	// cursor is in a gap — linear fallback
	return timeOffsetMs + (x / W) * totalMs;
}

function formatMs(ms: number): string {
	const totalSec = Math.max(0, Math.round(ms / 1000));
	const min = Math.floor(totalSec / 60);
	const sec = totalSec % 60;
	return `${min}:${sec.toString().padStart(2, "0")}`;
}

// ── Exported utilities used by ProctoringVideoPlayer ─────────────────────────

export function hasAttemptQuestionEvents(events: VideoEvent[]): boolean {
	return events.some(
		(e) =>
			e.type === "assessment" && e.name === "attempt question" && typeof e.details?.metadata?.duration === "number",
	);
}

export function getCurrentAttemptQuestionNo(events: VideoEvent[], curTimeMs: number): number | null {
	const ev = events.find(
		(e) =>
			e.type === "assessment" &&
			e.name === "attempt question" &&
			typeof e.details?.metadata?.duration === "number" &&
			curTimeMs >= e.time * 1000 &&
			curTimeMs < e.time * 1000 + (e.details.metadata.duration as number),
	);
	return ev ? (ev.details.questionNo as number) : null;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface QuestionAttemptSeekbarProps {
	events: VideoEvent[];
	curTimeMs: number;
	durationMs: number | undefined;
	timeOffsetMs: number;
	onSeekbarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	inFullscreen: boolean;
	isPlaying: boolean;
	children?: React.ReactNode;
}

export function QuestionAttemptSeekbar({
	events,
	curTimeMs,
	durationMs,
	timeOffsetMs,
	onSeekbarChange,
	inFullscreen,
	isPlaying,
	children,
}: QuestionAttemptSeekbarProps) {
	const [hoverState, setHoverState] = useState<{ index: number; x: number; ms: number } | null>(null);

	// Per the W3C ARIA APG media seek slider pattern, aria-valuenow/valuetext should only change
	// on user interaction (seek), not on every automatic playback tick — otherwise a focused
	// screen reader gets interrupted several times a second. Freeze the announced value while
	// playing; resync it on pause and on explicit seeks.
	const [announcedTimeMs, setAnnouncedTimeMs] = useState(curTimeMs);
	useEffect(() => {
		if (!isPlaying) {
			setAnnouncedTimeMs(curTimeMs);
		}
	}, [curTimeMs, isPlaying]);

	const handleSeekbarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setAnnouncedTimeMs(e.target.valueAsNumber);
		onSeekbarChange(e);
	};

	// Measure the track's actual pixel width so gap arithmetic is correct.
	// Uses a callback ref (state, not a plain ref) so the effect re-runs once the
	// track div actually mounts — it may not exist yet on first render if durationMs
	// (below) is still falsy at that point, e.g. while waiting on video metadata.
	const [trackEl, setTrackEl] = useState<HTMLDivElement | null>(null);
	const [trackWidth, setTrackWidth] = useState(0);
	useEffect(() => {
		if (!trackEl) return;
		const ro = new ResizeObserver(() => setTrackWidth(trackEl.getBoundingClientRect().width));
		ro.observe(trackEl);
		setTrackWidth(trackEl.getBoundingClientRect().width);
		return () => ro.disconnect();
	}, [trackEl]);

	if (!durationMs) return null;
	const totalMs = durationMs - timeOffsetMs;
	if (totalMs <= 0) return null;

	const fillPercent = (seg: QuestionSegment) => {
		if (curTimeMs <= seg.startMs) return 0;
		if (curTimeMs >= seg.endMs) return 100;
		return ((curTimeMs - seg.startMs) / (seg.endMs - seg.startMs)) * 100;
	};

	const segments = buildSegments(events, durationMs, timeOffsetMs);

	// Gap-aware thumb position
	const thumbPct = msToTrackPercent(curTimeMs, segments, totalMs, trackWidth, timeOffsetMs);

	// Hover detection using pixel ranges so gaps are dead zones
	const handleMouseMove = (e: React.MouseEvent<HTMLInputElement>) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const W = rect.width;
		if (segments.length === 0) {
			const ms = timeOffsetMs + (x / W) * totalMs;
			setHoverState({ index: 0, x, ms });
			return;
		}

		const ranges = segmentPixelRanges(segments, totalMs, W);

		let foundIdx: number | null = null;
		for (let i = 0; i < ranges.length; i++) {
			if (x >= ranges[i].startPx && x < ranges[i].endPx) {
				foundIdx = i;
				break;
			}
		}

		if (foundIdx !== null) {
			const ms = pixelToMs(x, ranges, segments, timeOffsetMs, totalMs, W);
			setHoverState({ index: foundIdx, x, ms });
		} else {
			setHoverState(null);
		}
	};

	if (segments.length === 0) {
		const seg: QuestionSegment = { questionNo: null, startMs: timeOffsetMs, endMs: durationMs };
		const fill = fillPercent(seg);

		return (
			<div className={styles.wrapper}>
				<div className={styles.questionAttemptSeekbar}>
					<div className={styles.questionAttemptSeekbarTrack} ref={setTrackEl}>
						<div
							className={`${addFullScreenClass(styles.questionAttemptSeekbarSegment, inFullscreen)}`}
							style={{ flex: 1 }}
						>
							{fill > 0 && <div className={styles.fill} style={{ width: `${fill}%` }} />}
						</div>
					</div>

					{hoverState && (
						<div className={addFullScreenClass(styles.tooltip, inFullscreen)} style={{ left: hoverState.x }}>
							<div style={{ width: "100%", textAlign: "center" }}>{formatMs(hoverState.ms - timeOffsetMs)}</div>
						</div>
					)}

					{/* Transparent native range input — handles seeking + hover detection */}
					<input
						type="range"
						className={styles.seekInput}
						min={timeOffsetMs}
						max={durationMs}
						value={announcedTimeMs}
						onChange={handleSeekbarChange}
						onMouseMove={handleMouseMove}
						onMouseLeave={() => setHoverState(null)}
						aria-label={i18n.t(
							"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.mediaProgressSlider",
						)}
						aria-valuemin={0}
						aria-valuemax={Math.floor(totalMs / 1000)}
						aria-valuenow={Math.floor((announcedTimeMs - timeOffsetMs) / 1000)}
						aria-valuetext={getDurationAriaLabelFromMs(announcedTimeMs - timeOffsetMs)}
						step="any"
					/>

					{/* Playhead circle */}
					<div
						className={addFullScreenClass(styles.thumb, inFullscreen)}
						aria-hidden="true"
						style={{ left: `${thumbPct}%` }}
					/>

					{/* SeekbarEvents markers */}
					{children}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.questionAttemptSeekbar}>
				<div className={styles.questionAttemptSeekbarTrack} ref={setTrackEl}>
					{segments.map((seg, i) => {
						const w = seg.endMs - seg.startMs;
						const fill = fillPercent(seg);
						const isHovered = hoverState?.index === i;

						return (
							<div
								key={i}
								className={`${addFullScreenClass(styles.questionAttemptSeekbarSegment, inFullscreen)} ${isHovered ? addFullScreenClass(styles.segHovered, inFullscreen) : ""}`}
								style={{ flex: w }}
							>
								{fill > 0 && <div className={styles.fill} style={{ width: `${fill}%` }} />}
							</div>
						);
					})}
				</div>

				{/* Hover tooltip — only for question segments */}
				{hoverState && (
					<div className={addFullScreenClass(styles.tooltip, inFullscreen)} style={{ left: hoverState.x }}>
						{segments[hoverState.index].questionNo !== null && (
							<div>
								{i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.question", {
									number: segments[hoverState.index].questionNo,
								})}
							</div>
						)}
						<div style={{ width: "100%", textAlign: "center" }}>{formatMs(hoverState.ms - timeOffsetMs)}</div>
					</div>
				)}

				{/* Transparent native range input — handles seeking + hover detection */}
				<input
					type="range"
					className={styles.seekInput}
					min={timeOffsetMs}
					max={durationMs}
					value={announcedTimeMs}
					onChange={handleSeekbarChange}
					onMouseMove={handleMouseMove}
					onMouseLeave={() => setHoverState(null)}
					aria-label={i18n.t(
						"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.mediaProgressSlider",
					)}
					aria-valuemin={0}
					aria-valuemax={Math.floor(totalMs / 1000)}
					aria-valuenow={Math.floor((announcedTimeMs - timeOffsetMs) / 1000)}
					aria-valuetext={getDurationAriaLabelFromMs(announcedTimeMs - timeOffsetMs)}
					step="any"
				/>

				{/* Playhead circle */}
				<div
					aria-hidden="true"
					className={addFullScreenClass(styles.thumb, inFullscreen)}
					style={{ left: `${thumbPct}%` }}
				/>

				{/* SeekbarEvents markers */}
				{children}
			</div>
		</div>
	);
}
